import { z } from 'zod'
import { PackageRulesSchema, PromotionSchema } from '@reward-platform/shared-schemas'
import { isEmpty, isObject } from 'lodash'
import {
  AddressSchema,
  CarHireItemType,
  CarHireLocationSchema,
  DateSchema,
  ExperienceItemType,
  HotelItemType,
  FlightItemType,
  MonetaryAmountSchema,
  RoomSchema,
  VehicleSchema,
  OrderLineStatusSchema,
  ISODateStringSchema,
  TaxDetailsSchema,
  CarHirePolicySchema,
  ExperienceCancellationPolicySchema,
  ExperienceDurationSchema,
  ExperienceLocationSchema,
  TravelerGroupSchema,
  FlightJourneySchema,
} from '../common'
import {
  createSelectedPaymentOptionsSchema,
  CreateSelectedPaymentOptionsSchemaParams,
  safeCreateTravelerDetailsSchema,
  TravelerDetails,
} from '../form-data'
import { safeGetTravelerContext } from '../helpers/traveler-context'
import { createAdditionalDetailsSchema } from '../form-data/additional-details'
import { resolveAdditionalDetailsContext } from '../helpers/additional-details-context'
import {
  createPaymentInformationFormSchema,
  CreatePaymentInformationFormSchemaParams,
} from '../form-data/payment-details'
import { Basket } from '../basket'

type CreateOrderSchemaParams = CreatePaymentInformationFormSchemaParams &
  CreateSelectedPaymentOptionsSchemaParams & {
    basket: Basket
    travelerInformation?: TravelerDetails
    isFullAviosPayment: boolean
  }

// TODO: Add validation logic to check that travelers in basket match with the ones
// in traveler and hotel information etc.
export const createOrderSchema = (params: CreateOrderSchemaParams) => {
  const travelerContext = safeGetTravelerContext(params.basket)
  const { travelerInformation } = params
  const travelerInformationSchema =
    travelerContext && safeCreateTravelerDetailsSchema(travelerContext)

  const additionalDetailsSchema = createAdditionalDetailsSchema(
    resolveAdditionalDetailsContext({
      ...params,
      travelerDetails: travelerInformation as TravelerDetails,
    })
  )

  const paymentInformationSchema = createPaymentInformationFormSchema({
    ...params,
    isExtendedPaymentInformationForm: true,
  }).optional()

  return z.object({
    travelerInformation: travelerInformationSchema ?? z.undefined().optional(),
    paymentInformation: paymentInformationSchema,
    additionalDetails: additionalDetailsSchema,
    selectedPaymentOptions: createSelectedPaymentOptionsSchema(params),
  })
}

export type CreateOrder = z.infer<ReturnType<typeof createOrderSchema>>

export const OrderStatusSchema = z.enum([
  'OPENED',
  'CONFIRMED',
  'HELD',
  'FAILED',
  'CANCELLED',
  'CLOSED',
  'SUSPENDED',
])

const TravelerSchema = z.object({
  age: z.number().optional(),
  ageGroup: z.any().optional(),
  dateOfBirth: z.string().optional(),
  roomGroup: z.number().optional(),
})

const SecureInstructionSchema = z.object({
  jwt: z.string(),
  redirectUrl: z.string(),
  terminationUrl: z.string(),
})

export const PaymentStatusCodeSchema = z.enum([
  'INITIALIZED',
  'SUSPENDED',
  'AUTHORIZED',
  'REFUNDED',
  'FAILED',
  'CLOSED',
])

export const PaymentTypeCode = z.enum(['RW', 'CC'])

export const OrderItemTypeSchema = z.enum(['HOTEL', 'EXPERIENCE', 'CAR_HIRE', 'FLIGHT'])
export type OrderItemType = z.infer<typeof OrderItemTypeSchema>

const OrderSurchargesBreakdown = z.object({
  feeCode: z.string().optional(),
  feeName: z.string().optional(),
  amount: MonetaryAmountSchema,
})

const OrderSurchargesBreakdownAndTotalSchema = z.object({
  total: MonetaryAmountSchema,
  breakdown: z.array(OrderSurchargesBreakdown),
})

export type OrderSurchargesBreakdownAndTotal = z.infer<
  typeof OrderSurchargesBreakdownAndTotalSchema
>

const OrderPriceBreakdownSchema = z.object({
  cashSpent: MonetaryAmountSchema.optional(),
  aviosSpent: z.number().optional(),
  aviosCollect: z.number().optional(),
  baseLoyalty: z.number().optional(),
  taxes: MonetaryAmountSchema.optional(),
  taxDetails: z.array(TaxDetailsSchema).optional(),
  isSalesTax: z.boolean(),
  surcharges: OrderSurchargesBreakdownAndTotalSchema.optional(),
  totalCashIncludingTax: MonetaryAmountSchema.optional(),
  promotions: z.array(PromotionSchema).optional(),
})

export type OrderPriceBreakdown = z.infer<typeof OrderPriceBreakdownSchema>

const BaseOrderItemSchema = z.object({
  id: z.string().min(1),
  type: OrderItemTypeSchema,
  status: OrderLineStatusSchema.optional(),
  bundleIds: z.string().array(),
  orderPriceBreakdown: OrderPriceBreakdownSchema.optional(),
})

export type BaseOrderItem = z.infer<typeof BaseOrderItemSchema>

export const HotelOrderItemSchema = BaseOrderItemSchema.extend({
  type: z.literal(OrderItemTypeSchema.enum.HOTEL),
  name: z.string().min(1),
  room: RoomSchema,
  address: AddressSchema,
  startDate: ISODateStringSchema,
  endDate: ISODateStringSchema,
  packageRules: PackageRulesSchema,
})

export type HotelOrderItem = z.infer<typeof HotelOrderItemSchema>

const CarHireExtraSchema = z.object({
  serviceRef: z.string(),
  serviceSubType: z.string(),
  name: z.string(),
  serviceCategory: z.string().optional(),
  description: z.string().optional(),
  selectedQuantity: z.number(),
  maxAllowedQuantity: z.number(),
})

export const CarHireOrderItemSchema = BaseOrderItemSchema.extend({
  type: z.literal(OrderItemTypeSchema.enum.CAR_HIRE),
  vehicle: VehicleSchema,
  pickUpLocation: CarHireLocationSchema,
  dropOffLocation: CarHireLocationSchema,
  extras: z.array(CarHireExtraSchema),
  startDate: ISODateStringSchema,
  endDate: ISODateStringSchema,
  locationPolicy: CarHirePolicySchema,
  rentalPolicy: CarHirePolicySchema,
  locationRentalPolicy: CarHirePolicySchema,
  vendorName: z.string().optional(),
})
export type CarHireOrderItem = z.infer<typeof CarHireOrderItemSchema>

export const ExperienceOrderItemSchema = BaseOrderItemSchema.extend({
  type: z.literal(OrderItemTypeSchema.enum.EXPERIENCE),
  name: z.string().min(1),
  travelers: TravelerGroupSchema.array(),
  startLocations: ExperienceLocationSchema.array().optional(),
  days: z.number().optional(),
  experienceType: z.string().optional(),
  endLocations: ExperienceLocationSchema.array().optional(),
  duration: ExperienceDurationSchema.optional(),
  status: OrderLineStatusSchema.optional(),
  additionalInfo: z.string().array().optional(),
  cancellationPolicy: ExperienceCancellationPolicySchema,
  startDate: ISODateStringSchema.optional(),
  startTime: z.string().optional(),
})

export type ExperienceOrderItem = z.infer<typeof ExperienceOrderItemSchema>

export const FlightOrderItemSchema = BaseOrderItemSchema.extend({
  type: z.literal(OrderItemTypeSchema.enum.FLIGHT),
  inboundFlightJourney: FlightJourneySchema.optional(),
  outboundFlightJourney: FlightJourneySchema,
})
export type FlightOrderItem = z.infer<typeof FlightOrderItemSchema>

const OrderItemSchema = z.union([
  HotelOrderItemSchema,
  ExperienceOrderItemSchema,
  CarHireOrderItemSchema,
  FlightOrderItemSchema,
])

export type OrderItem = z.infer<typeof OrderItemSchema>

const OrderItemsSchema = z.object({
  [HotelItemType]: z.array(HotelOrderItemSchema),
  [ExperienceItemType]: z.array(ExperienceOrderItemSchema),
  [CarHireItemType]: z.array(CarHireOrderItemSchema),
  [FlightItemType]: z.array(FlightOrderItemSchema),
})

export type OrderItems = z.infer<typeof OrderItemsSchema>

const OrderPaymentBaseSchema = z.object({
  paymentId: z.string().min(1),
  statusCode: PaymentStatusCodeSchema.optional(),
})
export type OrderPaymentBase = z.infer<typeof OrderPaymentBaseSchema>

const OrderPaymentCCSchema = OrderPaymentBaseSchema.extend({
  typeCode: z.literal(PaymentTypeCode.enum.CC),
  secureInstruction: z.preprocess(
    (val) => (isObject(val) && isEmpty(val) ? undefined : val),
    SecureInstructionSchema.optional()
  ),
  stepUpWindowDimensions: z.string().optional(),
})
export type OrderPaymentCC = z.infer<typeof OrderPaymentCCSchema>

const OrderPaymentRWSchema = OrderPaymentBaseSchema.extend({
  typeCode: z.literal(PaymentTypeCode.enum.RW),
})

const OrderPaymentSchema = z.discriminatedUnion('typeCode', [
  OrderPaymentCCSchema,
  OrderPaymentRWSchema,
])
export type OrderPayment = z.infer<typeof OrderPaymentSchema>

export const OrderSchema = z.object({
  correlationId: z.string(),
  orderId: z.string(),
  travelers: TravelerSchema.array().optional(),
  status: OrderStatusSchema.optional(),
  creationDate: DateSchema,
  payments: z.array(OrderPaymentSchema),
  items: OrderItemsSchema.optional(),
  totalCashSpent: MonetaryAmountSchema.optional(),
  totalAviosSpent: z.number().optional(),
  totalAviosCollected: z.number().optional(),
  isAtolEligible: z.boolean().optional(),
})

export type Order = z.infer<typeof OrderSchema>
